import macro from 'vtk.js/Sources/macros';
import * as vtkMath from 'vtk.js/Sources/Common/Core/Math';
import stateGenerator from './state';
import vtkAbstractWidgetFactory from 'vtk.js/Sources/Widgets/Core/AbstractWidgetFactory';
import vtkArrowHandleRepresentation from 'vtk.js/Sources/Widgets/Representations/ArrowHandleRepresentation';
import vtkPlanePointManipulator from 'vtk.js/Sources/Widgets/Manipulators/PlaneManipulator';
import vtkPolyLineRepresentation from 'vtk.js/Sources/Widgets/Representations/PolyLineRepresentation';
import widgetBehavior from './behavior';
import { Behavior } from 'vtk.js/Sources/Widgets/Representations/WidgetRepresentation/Constants';
import { ViewTypes } from 'vtk.js/Sources/Widgets/Core/WidgetManager/Constants';

// ----------------------------------------------------------------------------
// Factory
// ----------------------------------------------------------------------------

const handleRepresentationConfig = {
    visibilityFlagArray: [true, true],
    coincidentTopologyParameters: {
        Point: { factor: -1.0, offset: -1.0 },
        Line: { factor: -1.0, offset: -1.0 },
        Polygon: { factor: -3.0, offset: -3.0 },
    },
};

function vtkCustomAngleWidget(publicAPI, model) {
    model.classHierarchy.push('vtkCustomAngleWidget');

    const superClass = { ...publicAPI };

    model.methodsToLink = [
        'activeScaleFactor',
        'activeColor',
        'useActiveColor',
        'glyphResolution',
        'defaultScale',
        'scaleInPixels',
    ];

    publicAPI.getRepresentationsForViewType = (viewType) => {
        switch (viewType) {
            case ViewTypes.DEFAULT:
            case ViewTypes.GEOMETRY:
            case ViewTypes.SLICE:
            case ViewTypes.VOLUME:
            default:
                return [
                    // handle1 = arm1 endpoint (first click)
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['handle1'],
                        initialValues: { ...handleRepresentationConfig },
                    },
                    // handle2 = vertex (second click)
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['handle2'],
                        initialValues: { ...handleRepresentationConfig },
                    },
                    // handle3 = arm2 endpoint
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['handle3'],
                        initialValues: { ...handleRepresentationConfig },
                    },
                    // moveHandle = ghost handle following mouse
                    {
                        builder: vtkArrowHandleRepresentation,
                        labels: ['moveHandle'],
                        initialValues: { ...handleRepresentationConfig },
                    },
                    // PolyLine: draws arm1 → vertex → arm2 (V-shape), with moveHandle for live preview
                    {
                        builder: vtkPolyLineRepresentation,
                        labels: ['handle1', 'handle2', 'handle3', 'moveHandle'],
                        initialValues: {
                            behavior: Behavior.HANDLE,
                            lineThickness: 5
                        },
                    },
                ];
        }
    };

    // --- Public methods -------------------------------------------------------

    // Returns the angle in degrees, or -1 if the geometry is degenerate
    // (one or both arm vectors have zero length, meaning a handle hasn't been
    // placed away from the vertex yet). This lets callers distinguish a genuine
    // 0° angle (arms overlapping) from the "not-yet-placed" case.
    publicAPI.getAngle = () => {
        const vertex = model.widgetState.getHandle2().getOrigin();
        const arm1 = model.widgetState.getHandle1().getOrigin();
        const arm2 = model.widgetState.getHandle3().getOrigin();
        if (!vertex || !arm1 || !arm2) return -1;

        const v1 = vtkMath.subtract(arm1, vertex, []);
        const v2 = vtkMath.subtract(arm2, vertex, []);
        const mag1 = Math.sqrt(vtkMath.dot(v1, v1));
        const mag2 = Math.sqrt(vtkMath.dot(v2, v2));
        if (mag1 === 0 || mag2 === 0) return -1;

        const dot = vtkMath.dot(v1, v2);
        return Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2)))) * (180 / Math.PI);
    };

    publicAPI.setManipulator = (manipulator) => {
        superClass.setManipulator(manipulator);
        model.widgetState.getMoveHandle().setManipulator(manipulator);
        model.widgetState.getHandle2().setManipulator(manipulator);
        model.widgetState.getHandle1().setManipulator(manipulator);
        model.widgetState.getHandle3().setManipulator(manipulator);
    };

    // --------------------------------------------------------------------------
    // initialization
    // --------------------------------------------------------------------------

    model.widgetState.onBoundsChange((bounds) => {
        const center = [
            (bounds[0] + bounds[1]) * 0.5,
            (bounds[2] + bounds[3]) * 0.5,
            (bounds[4] + bounds[5]) * 0.5,
        ];
        model.widgetState.getMoveHandle().setOrigin(center);
    });

    // Default manipulator
    publicAPI.setManipulator(
        model.manipulator ||
        vtkPlanePointManipulator.newInstance({ useCameraNormal: true })
    );
}

// ----------------------------------------------------------------------------

const defaultValues = (initialValues) => ({
    behavior: widgetBehavior,
    widgetState: stateGenerator(initialValues),
    ...initialValues,
});

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
    Object.assign(model, defaultValues(initialValues));

    vtkAbstractWidgetFactory.extend(publicAPI, model, initialValues);
    macro.setGet(publicAPI, model, ['manipulator']);

    vtkCustomAngleWidget(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(extend, 'vtkCustomAngleWidget');

// ----------------------------------------------------------------------------

export default { newInstance, extend };
